/**
 * AI Suggestion Status Store
 *
 * Persists AI suggestion "ready" status in localStorage so the badge
 * survives page refreshes. Entries are cleared when the user reads
 * (opens) the conversation.
 */

const STORAGE_KEY = 'frontdesk.ai-suggestion-ready';

function getReadySet(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch (_e) {
    return new Set();
  }
}

function saveReadySet(set: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch (_e) {
    // localStorage full or unavailable — ignore
  }
}

/** Mark a conversation as having an AI suggestion ready */
export function markAISuggestionReady(conversationId: string): void {
  const set = getReadySet();
  set.add(conversationId);
  saveReadySet(set);
}

/** Clear the AI suggestion badge for a conversation (user read it) */
export function clearAISuggestionReady(conversationId: string): void {
  const set = getReadySet();
  set.delete(conversationId);
  saveReadySet(set);
}

/** Get all conversation IDs that have AI suggestions ready */
export function getAISuggestionReadyIds(): string[] {
  return Array.from(getReadySet());
}

const CONSUMED_STORAGE_KEY = 'frontdesk.ai-suggestion-consumed';

function getConsumedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(CONSUMED_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch (_e) {
    return new Set();
  }
}

function saveConsumedSet(set: Set<string>): void {
  try {
    // Cap size so localStorage does not grow forever
    const arr = Array.from(set);
    const trimmed = arr.length > 200 ? arr.slice(arr.length - 200) : arr;
    localStorage.setItem(CONSUMED_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (_e) {
    // ignore
  }
}

/** Consumed-store key for a suggestion in a conversation. Empty when there is nothing to key on. */
export function aiSuggestionKey(
  conversationId: string | number | null | undefined,
  suggestionId: string | null | undefined,
  text: string | null | undefined
): string {
  const conv = conversationId?.toString() || '';
  if (suggestionId) return `${conv}:id:${suggestionId}`;
  if (text) return `${conv}:text:${text.slice(0, 120)}`;
  return '';
}

/** Mark a suggestion draft as already sent/dismissed (do not offer it again) */
export function markAISuggestionConsumed(key: string): void {
  if (!key) return;
  const set = getConsumedSet();
  set.add(key);
  saveConsumedSet(set);
}

export function isAISuggestionConsumed(key: string): boolean {
  if (!key) return false;
  return getConsumedSet().has(key);
}

const AI_PROCESSED_READ_KEY = "frontdesk.ai-processed-read";
const AI_PROCESSED_READ_CAP = 400;

function getAiProcessedReadSet(): Set<string> {
  try {
    const raw = localStorage.getItem(AI_PROCESSED_READ_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch (_e) {
    return new Set();
  }
}

function saveAiProcessedReadSet(set: Set<string>): void {
  try {
    const arr = Array.from(set);
    const trimmed =
      arr.length > AI_PROCESSED_READ_CAP
        ? arr.slice(arr.length - AI_PROCESSED_READ_CAP)
        : arr;
    localStorage.setItem(AI_PROCESSED_READ_KEY, JSON.stringify(trimmed));
  } catch (_e) {
    // ignore
  }
}

/** Hide these AI-processed notification rows after Read all. */
export function markAIProcessedNotificationsRead(ids: string[]): void {
  const set = getAiProcessedReadSet();
  ids.forEach((id) => {
    if (id) set.add(String(id));
  });
  saveAiProcessedReadSet(set);
}

export function isAIProcessedNotificationRead(id: string): boolean {
  if (!id) return false;
  return getAiProcessedReadSet().has(String(id));
}
